import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getOrCreateAnonymousId, isAnonymousUser } from '@/utils/anonymousUserUtils';

/**
 * Hook per ottenere un userId valido per il sistema di chat.
 * Gestisce sia utenti autenticati che anonimi.
 * 
 * @returns {Object} Oggetto contenente userId e flag isAnonymous
 */
export const useUserId = () => {
  const { user, isAuthenticated } = useAuth();

  const userIdData = useMemo(() => {
    // Per utenti autenticati, usa l'ID di Supabase
    if (isAuthenticated && user?.id) {
      return {
        userId: user.id,
        isAnonymous: false
      };
    }
    
    // Per utenti non autenticati, genera/recupera ID anonimo
    const anonymousId = getOrCreateAnonymousId();
    return {
      userId: anonymousId,
      isAnonymous: true
    };
  }, [isAuthenticated, user?.id]);

  return userIdData;
};

/**
 * Hook helper per verificare se l'utente corrente è anonimo.
 * 
 * @returns {boolean} True se l'utente è anonimo
 */
export const useIsAnonymousUser = () => {
  const { userId } = useUserId();
  return isAnonymousUser(userId);
};

/**
 * Hook helper per ottenere solo l'userId.
 * 
 * @returns {string} ID utente (autenticato o anonimo)
 */
export const useUserIdOnly = () => {
  const { userId } = useUserId();
  return userId;
};
